import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Wrench, Plus, Trash, Calendar, DollarSign, Clock, Gear, Fan, Barricade, Eyedropper, Scissors, CaretRight } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import { UpgradeGate } from '../components/UpgradeGate';
import { PageWhy } from '../components/PageWhy';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const EQUIPMENT_TYPES = [
  { value: 'mower', label: 'Lawn Mower', icon: '🚜' },
  { value: 'trimmer', label: 'String Trimmer', icon: '✂️' },
  { value: 'blower', label: 'Leaf Blower', icon: '💨' },
  { value: 'spreader', label: 'Spreader', icon: '🌱' },
  { value: 'edger', label: 'Edger', icon: '📏' },
  { value: 'aerator', label: 'Aerator', icon: '🔧' },
  { value: 'dethatcher', label: 'Dethatcher', icon: '🪥' },
  { value: 'sprayer', label: 'Sprayer', icon: '💧' },
  { value: 'chainsaw', label: 'Chainsaw', icon: '🪚' },
  { value: 'other', label: 'Other', icon: '🔩' },
];

const SERVICE_TYPES = [
  { value: 'oil_change', label: 'Oil Change', icon: '🛢️' },
  { value: 'blade_sharpen', label: 'Blade Sharpening', icon: '⚔️' },
  { value: 'spark_plug', label: 'Spark Plug Replacement', icon: '⚡' },
  { value: 'air_filter', label: 'Air Filter Replacement', icon: '💨' },
  { value: 'belt_replace', label: 'Belt Replacement', icon: '🔗' },
  { value: 'battery_install', label: 'Battery Install/Replace', icon: '🔋' },
  { value: 'calibration', label: 'Calibration', icon: '📊' },
  { value: 'winterize', label: 'Winterization', icon: '❄️' },
  { value: 'fuel_system', label: 'Fuel System Service', icon: '⛽' },
  { value: 'general_service', label: 'General Service', icon: '🔧' },
];

export const EquipmentPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [equipment, setEquipment] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedMessage, setLockedMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [spreaderSettings, setSpreaderSettings] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  
  // Form states
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    equipment_type: 'mower',
    brand: '',
    model_number: '',
    purchase_date: '',
    notes: ''
  });
  
  const [newService, setNewService] = useState({
    service_type: 'oil_change',
    service_date: new Date().toISOString().split('T')[0],
    cost: '',
    notes: '',
    next_service_date: ''
  });

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment`, { withCredentials: true });
      // Handle new response format with tier gating
      if (res.data.is_locked) {
        setIsLocked(true);
        setLockedMessage(res.data.upgrade_message || 'Equipment Log requires a paid subscription');
        setEquipment([]);
      } else if (res.data.equipment) {
        setEquipment(res.data.equipment);
        setIsLocked(false);
      } else {
        // Backwards compatibility
        setEquipment(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      if (error.response?.status === 403) {
        setIsLocked(true);
        setLockedMessage(error.response?.data?.detail?.message || 'Equipment Log requires a paid subscription');
      } else {
        toast.error('Failed to load equipment');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceHistory = async (equipmentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment/${equipmentId}/services`, { withCredentials: true });
      setServiceHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch service history:', error);
    }
  };

  const fetchSpreaderSettings = async (equipmentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/equipment/${equipmentId}/spreader-settings`, { withCredentials: true });
      setSpreaderSettings(res.data.settings || []);
    } catch (error) {
      console.error('Failed to fetch spreader settings:', error);
      setSpreaderSettings([]);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/equipment`, newEquipment, { withCredentials: true });
      setEquipment([res.data, ...equipment]);
      setShowAddDialog(false);
      setNewEquipment({ name: '', equipment_type: 'mower', brand: '', model_number: '', purchase_date: '', notes: '' });
      toast.success('Equipment added successfully!');
    } catch (error) {
      toast.error('Failed to add equipment');
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!confirm('Delete this equipment and all its service history?')) return;
    try {
      await axios.delete(`${API_URL}/api/equipment/${equipmentId}`, { withCredentials: true });
      setEquipment(equipment.filter(eq => eq.id !== equipmentId));
      if (selectedEquipment?.id === equipmentId) {
        setSelectedEquipment(null);
        setServiceHistory([]);
        setSpreaderSettings([]);
      }
      toast.success('Equipment deleted');
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!selectedEquipment) return;
    
    try {
      const payload = {
        ...newService,
        equipment_id: selectedEquipment.id,
        cost: newService.cost ? parseFloat(newService.cost) : null
      };
      const res = await axios.post(
        `${API_URL}/api/equipment/${selectedEquipment.id}/services`,
        payload,
        { withCredentials: true }
      );
      setServiceHistory([res.data, ...serviceHistory]);
      setShowServiceDialog(false);
      setNewService({ service_type: 'oil_change', service_date: new Date().toISOString().split('T')[0], cost: '', notes: '', next_service_date: '' });
      toast.success('Service logged successfully!');
      fetchEquipment(); // Refresh to update service counts
    } catch (error) {
      toast.error('Failed to log service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await axios.delete(`${API_URL}/api/services/${serviceId}`, { withCredentials: true });
      setServiceHistory(serviceHistory.filter(s => s.id !== serviceId));
      fetchEquipment();
      toast.success('Service log deleted');
    } catch (error) {
      toast.error('Failed to delete service log');
    }
  };

  const selectEquipment = (eq) => {
    setSelectedEquipment(eq);
    setActiveTab('services');
    fetchServiceHistory(eq.id);
    // If it's a spreader, fetch spreader settings
    if (eq.equipment_type === 'spreader') {
      fetchSpreaderSettings(eq.id);
    } else {
      setSpreaderSettings([]);
    }
  };

  const getEquipmentIcon = (type) => {
    const found = EQUIPMENT_TYPES.find(t => t.value === type);
    return found ? found.icon : '🔧';
  };

  const getServiceIcon = (type) => {
    const found = SERVICE_TYPES.find(t => t.value === type);
    return found ? found.icon : '🔧';
  };

  const getServiceLabel = (type) => {
    const found = SERVICE_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div data-testid="equipment-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <UpgradeGate feature="equipment_log" featureLabel="Equipment Log">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Wrench weight="duotone" size={36} className="text-primary" />
              Equipment Log
            </h1>
            <p className="text-muted-foreground mt-1">Track maintenance and service history for your lawn care equipment</p>
            <PageWhy testId="equipment-page-why">Know when your mower was last serviced and what it cost — so your gear is ready when the season turns, not broken in the middle of a stripe.</PageWhy>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-equipment-btn">
                <Plus weight="bold" size={18} className="mr-2" />
                Add Equipment
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Equipment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddEquipment} className="space-y-4">
                <div>
                  <Label>Equipment Name *</Label>
                  <Input
                    data-testid="equipment-name-input"
                    value={newEquipment.name}
                    onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                    placeholder="e.g., Honda HRX217 Mower"
                    required
                  />
                </div>
                <div>
                  <Label>Equipment Type *</Label>
                  <Select
                    value={newEquipment.equipment_type}
                    onValueChange={(val) => setNewEquipment({ ...newEquipment, equipment_type: val })}
                  >
                    <SelectTrigger data-testid="equipment-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Brand</Label>
                    <Input
                      value={newEquipment.brand}
                      onChange={(e) => setNewEquipment({ ...newEquipment, brand: e.target.value })}
                      placeholder="e.g., Honda, Stihl"
                    />
                  </div>
                  <div>
                    <Label>Model Number</Label>
                    <Input
                      value={newEquipment.model_number}
                      onChange={(e) => setNewEquipment({ ...newEquipment, model_number: e.target.value })}
                      placeholder="e.g., HRX217K6VKA"
                    />
                  </div>
                </div>
                <div>
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={newEquipment.purchase_date}
                    onChange={(e) => setNewEquipment({ ...newEquipment, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input
                    value={newEquipment.notes}
                    onChange={(e) => setNewEquipment({ ...newEquipment, notes: e.target.value })}
                    placeholder="Any additional notes..."
                  />
                </div>
                <Button type="submit" className="w-full">Add Equipment</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Equipment List */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">My Equipment</h2>
            
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : equipment.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Wrench size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">No equipment added yet</p>
                  <Button variant="outline" onClick={() => setShowAddDialog(true)}>
                    Add Your First Equipment
                  </Button>
                </CardContent>
              </Card>
            ) : (
              equipment.map((eq) => (
                <Card
                  key={eq.id}
                  data-testid={`equipment-card-${eq.id}`}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedEquipment?.id === eq.id ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => selectEquipment(eq)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">{getEquipmentIcon(eq.equipment_type)}</div>
                        <div>
                          <h3 className="font-semibold">{eq.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {eq.brand} {eq.model_number}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs bg-secondary px-2 py-1 rounded">
                              {eq.service_count || 0} services
                            </span>
                            {eq.last_service && (
                              <span className="text-xs text-muted-foreground">
                                Last: {formatDate(eq.last_service.service_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <CaretRight size={20} className="text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Service History Panel */}
          <div className="md:col-span-2">
            {selectedEquipment ? (
              <Card>
                <CardHeader className="flex flex-row items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">{getEquipmentIcon(selectedEquipment.equipment_type)}</span>
                      {selectedEquipment.name}
                    </CardTitle>
                    <CardDescription>
                      {selectedEquipment.brand} {selectedEquipment.model_number}
                      {selectedEquipment.purchase_date && (
                        <span className="ml-2">• Purchased {formatDate(selectedEquipment.purchase_date)}</span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
                      <DialogTrigger asChild>
                        <Button data-testid="log-service-btn">
                          <Plus weight="bold" size={18} className="mr-2" />
                          Log Service
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Log Service for {selectedEquipment.name}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddService} className="space-y-4">
                          <div>
                            <Label>Service Type *</Label>
                            <Select
                              value={newService.service_type}
                              onValueChange={(val) => setNewService({ ...newService, service_type: val })}
                            >
                              <SelectTrigger data-testid="service-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SERVICE_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.icon} {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Service Date *</Label>
                            <Input
                              type="date"
                              value={newService.service_date}
                              onChange={(e) => setNewService({ ...newService, service_date: e.target.value })}
                              required
                            />
                          </div>
                          <div>
                            <Label>Cost ($)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newService.cost}
                              onChange={(e) => setNewService({ ...newService, cost: e.target.value })}
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <Label>Notes</Label>
                            <Input
                              value={newService.notes}
                              onChange={(e) => setNewService({ ...newService, notes: e.target.value })}
                              placeholder="Any additional notes..."
                            />
                          </div>
                          <div>
                            <Label>Next Service Reminder Date (optional)</Label>
                            <Input
                              type="date"
                              value={newService.next_service_date}
                              onChange={(e) => setNewService({ ...newService, next_service_date: e.target.value })}
                            />
                          </div>
                          <Button type="submit" className="w-full">Log Service</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteEquipment(selectedEquipment.id)}
                    >
                      <Trash size={18} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {selectedEquipment.notes && (
                    <p className="text-sm text-muted-foreground mb-4 p-3 bg-secondary/50 rounded-lg">
                      {selectedEquipment.notes}
                    </p>
                  )}
                  
                  {/* Tabs for Spreaders */}
                  {selectedEquipment.equipment_type === 'spreader' ? (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                      <TabsList className="mb-4">
                        <TabsTrigger value="services">Service History</TabsTrigger>
                        <TabsTrigger value="settings" className="relative">
                          Spreader Settings
                          {spreaderSettings.length > 0 && (
                            <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                              {spreaderSettings.length}
                            </span>
                          )}
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="services">
                        {renderServiceHistory()}
                      </TabsContent>
                      
                      <TabsContent value="settings">
                        {renderSpreaderSettings()}
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <>
                      <h3 className="font-semibold mb-4">Service History</h3>
                      {renderServiceHistory()}
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-16">
                  <Gear size={64} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">Select Equipment</h3>
                  <p className="text-muted-foreground">
                    Choose equipment from the list to view service history
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      </UpgradeGate>
    </div>
  );
  
  function renderServiceHistory() {
    return (
      <>
        {serviceHistory.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <Clock size={32} className="mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">No service history yet</p>
            <p className="text-sm text-muted-foreground mt-1">Log your first service to start tracking</p>
          </div>
        ) : (
          <div className="space-y-3">
            {serviceHistory.map((service) => (
              <div
                key={service.id}
                data-testid={`service-log-${service.id}`}
                className="flex items-start justify-between p-4 border rounded-lg hover:bg-secondary/30 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{getServiceIcon(service.service_type)}</div>
                  <div>
                    <h4 className="font-medium">{getServiceLabel(service.service_type)}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(service.service_date)}
                    </p>
                    {service.notes && (
                      <p className="text-sm text-muted-foreground mt-1">{service.notes}</p>
                    )}
                    {service.next_service_date && (
                      <p className="text-xs text-primary mt-1">
                        Next service: {formatDate(service.next_service_date)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {service.cost > 0 && (
                    <span className="text-sm font-medium text-green-600">
                      ${service.cost.toFixed(2)}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => handleDeleteService(service.id)}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Service Summary */}
        {serviceHistory.length > 0 && (
          <div className="mt-6 p-4 bg-secondary/50 rounded-lg">
            <h4 className="font-medium mb-2">Service Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{serviceHistory.length}</p>
                <p className="text-xs text-muted-foreground">Total Services</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  ${serviceHistory.reduce((sum, s) => sum + (s.cost || 0), 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground">Total Cost</p>
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {serviceHistory[0] ? formatDate(serviceHistory[0].service_date).split(',')[0] : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">Last Service</p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
  
  function renderSpreaderSettings() {
    const PRODUCT_TYPE_ICONS = {
      'fertilizer': '🌱',
      'pre-emergent': '🛡️',
      'granular_insecticide': '🐛',
      'seed': '🌾',
      'lime': 'ite',
      'other': '📦'
    };
    
    return (
      <div>
        {spreaderSettings.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <Gear size={32} className="mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">No spreader settings found for this model</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add your brand ({selectedEquipment?.brand}) to find matching settings
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.href = '/spreader-settings'}
            >
              Browse All Settings
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Found {spreaderSettings.length} settings for your {selectedEquipment?.brand} spreader
            </p>
            <div className="grid gap-3">
              {spreaderSettings.map((setting) => (
                <div
                  key={setting.id}
                  className="p-4 border rounded-lg hover:bg-secondary/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{PRODUCT_TYPE_ICONS[setting.product_type] || '📦'}</span>
                        <h4 className="font-medium">{setting.product_name}</h4>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {setting.spreader_brand} {setting.spreader_model}
                      </p>
                    </div>
                    <div className="text-center bg-primary/10 rounded-lg px-4 py-2">
                      <p className="text-xs text-muted-foreground">Setting</p>
                      <p className="text-2xl font-bold text-primary">{setting.setting_value}</p>
                    </div>
                  </div>
                  {setting.rate_per_1000_sqft && (
                    <p className="text-sm mt-2">
                      <span className="text-muted-foreground">Rate:</span> {setting.rate_per_1000_sqft}
                    </p>
                  )}
                  {setting.notes && (
                    <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded mt-2">
                      💡 {setting.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="mt-4 w-full"
              onClick={() => window.location.href = '/spreader-settings'}
            >
              View All Spreader Settings
            </Button>
          </>
        )}
      </div>
    );
  }
};
